/**
 * Cookie Consents scripts
 *
 * This script is ONLY loaded when the cookie consent gate is switched on - see the
 * `consentGateEnabled` check in main/_layout/main.twig and simple.twig. When the gate
 * is off, SEOmatic renders its own script container server-side and this file never
 * loads. So everything here can assume consent IS required; its only job is to inject
 * the scripts the visitor has approved.
 *
 * Scripts from SEOmatic are injected IF the consent is given. The consent must be named
 * performance for the SEOmatic scripts.
 * Note: the limitation of this is that GoogleTagManager itself is injected if performance
 * is given but scripts that may be loaded by GTM are not filtered out - all will be run
 * IF GTM is allowed.
 *
 * THE CONSENT CHECK LIVES IN ONE PLACE: the `hasConsent()` helper below. Every decision
 * about whether a third-party script may run goes through it.
 */
(() => {
  'use strict';

    const COOKIE_NAME = 'cookie_consent'

    const GRAPHQL_TOKEN = 'Bearer 1PXSyvyoegXUGOAF67HJkMSOQRo3Y_j_'

    const getConsent = () => {
        const match = document.cookie.match(new RegExp('(^| )' + COOKIE_NAME + '=([^;]+)'))
        if (!match) return null
        try {
            // Value is base64-encoded JSON; a malformed/tampered value is treated as "no consent".
            return JSON.parse(atob(decodeURIComponent(match[2])))
        } catch {
            return null
        }
    }

    const setConsent = (consent: any, container: HTMLElement) => {
        document.cookie = `${COOKIE_NAME}=${encodeURIComponent(btoa(JSON.stringify(consent)))}; path=/; max-age=31536000`
        container.classList.remove('active')
    }

  /**
   * Handle accepting cookies
   */
  const handleAcceptAll = (cookieGroups: any, container: HTMLElement) => {
      const consent = Object.fromEntries(
          Object.keys(cookieGroups).map(key => [key, true])
      )
      setConsent(consent, container)
  }

    /**
     * Handle rejecting cookies
     */
  const handleRejectAll = (cookieGroups: any, container: HTMLElement) => {
        const consent = Object.fromEntries(
            Object.keys(cookieGroups).map(key => [key, false])
        )
        setConsent(consent, container)
  }

    const getSeoScripts = async () => {

        const query = `{
          seomatic(uri: "/", siteId: 2, environment: live, asArray: true) {
            metaScriptContainer
          }
        }`

        const response = await fetch("/actions/graphql/api", {
            method:  "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest",
                "Authorization": GRAPHQL_TOKEN
            },
            body: JSON.stringify({ query }),
        })
            .then(res => res.json())
            .then(data => {
                return data.data
            })
        ;

        if (response.seomatic) {
            return JSON.parse(response.seomatic.metaScriptContainer)
        } else {
            return {}
        }
    };

    /**
     * Fetch SEOmatic's meta script containers and inject the analytics ones.
     * Only reached once `hasConsent('performance')` has returned true.
     */
    const injectSeoScripts = () => getSeoScripts().then(metaContainers => {

        const analyticsScripts = ['gtag', 'googleTagManager', 'facebookPixel', 'linkedInInsight', 'hubSpot', 'pinterestTag', 'fathom', 'matomo', 'plausible', 'googleAnalytics' ]

        Object.entries(metaContainers).forEach(([key, container]: [string, any]) => {
            if (analyticsScripts.includes(key) && container.script) {
                const newScript = document.createElement('script')
                newScript.textContent = container.script
                document.head.appendChild(newScript)
            }
            // We skip noscript since JS is already running
        })
    })

    const consent = getConsent()

    /**
     * ===== THE CONSENT CHECK =====
     *
     * The single place that decides whether scripts in a given cookie group may run.
     * True only when the visitor's `cookie_consent` cookie grants that group explicitly.
     *
     * No cookie yet (banner still showing) and "Reject" (cookie with the group set to
     * false) both return false - nothing is assumed on the visitor's behalf.
     */
    const hasConsent = (group: string): boolean => consent?.[group] === true

    if (hasConsent('analytics')) {
        // Insert custom analytics scripts here

    }

    // Get the SEOmatic scripts
    if (hasConsent('performance')) {
        injectSeoScripts()
    }

    /**
     * Other scripts that are injected directly should be included here is the consent matches
     *
     * Example:
     *
     * if (hasConsent('advertising')) {
     *     const newScript = document.createElement('script')
     *     newScript.textContent = 'console.log("Advertising consent given")'
     *     document.head.appendChild(newScript)
     * }
     */

    // If using Blitz wrap in an event listener for the `afterBlitzInjectAll` event

    const ready = (fn: () => void) => {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', fn, { once: true })
        } else {
            fn()
        }
    }

    // The banner markup sits at the end of <body>, after this (async) script tag,
    // so wait for parsing to finish before looking for it.
    ready(() => {

        // Find the consent container. Absent once the visitor has made a choice.
        const consentContainer = document.querySelector('[data-consents]') as HTMLElement

        if (consentContainer) {

            setTimeout(() => consentContainer.classList.add('active'), 500)

            const cookieGroups = JSON.parse(consentContainer.dataset.consents ?? '{}')

            const buttons = consentContainer.querySelectorAll('[data-consent-button]')

            const preferencesButton = consentContainer.querySelector('[data-consent-preferences]') as HTMLButtonElement

            const options = consentContainer.querySelector('.consent-options') as HTMLElement

            // Create event listeners for each consent button
            buttons.forEach(button => {

                button.addEventListener('click', () => {
                    const consentType = button.getAttribute('data-consent-button')

                    if (consentType === 'accept') {
                        handleAcceptAll(cookieGroups, consentContainer)
                    } else if (consentType === 'reject') {
                        handleRejectAll(cookieGroups, consentContainer)
                    } else if (consentType === 'manage') {
                        options.classList.add('open')
                    }
                })

            })

            if (preferencesButton) {
                preferencesButton.addEventListener('click', () => {
                    const consent: Record<string, boolean> = {}

                    options.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach(checkbox => {
                        if (checkbox.name in cookieGroups) {
                            consent[checkbox.name] = checkbox.checked
                        }
                    })

                    setConsent(consent, consentContainer)
                })
            }

        }
    })
})();
